export const theme = {
  colors: {
    // Landing Page Primary Colors (Deep Navy Blue - matches #000052)
    primary: {
      50: '#F0F0FE',
      100: '#E1E1FD',
      200: '#C8C8FB',
      300: '#A5A5F6',
      400: '#7B7BEF',
      500: '#000052', // Main landing page primary color
      600: '#000047',
      700: '#00003D',
      800: '#000033',
      900: '#000029',
    },
    
    // Secondary accent (darker navy blue theme to complement primary)
    accent: {
      50: '#F0F0FE',
      100: '#E1E1FD',
      200: '#C8C8FB',
      300: '#A5A5F6',
      400: '#7B7BEF',
      500: '#000052',
      600: '#000047',
      700: '#00003D',
      800: '#000033',
      900: '#000029',
    },
    
    // Semantic Colors
    success: {
      100: '#DCFCE7',
      500: '#22C55E',
      600: '#16A34A',
      700: '#15803D',
    },
    warning: {
      100: '#FEF3C7',
      500: '#EAB308',
      600: '#CA8A04',
      700: '#A16207',
    },
    error: {
      100: '#FEE2E2',
      500: '#EF4444',
      600: '#DC2626',
      700: '#B91C1C',
    },
    info: {
      100: '#E1E1FD',
      500: '#000052',
      600: '#000047',
      700: '#00003D',
    },
    
    // Background System (matching landing page)
    background: '#FFFFFF',
    backgroundSecondary: '#F8FAFB', // matches landing hero background
    backgroundTertiary: '#F0F2F4', // matches landing main-gray
    surface: '#FFFFFF',
    surfaceHover: '#F8FAFB',
    
    // Text Hierarchy (matching landing page)
    textPrimary: '#111111', // matches landing heading-color
    textSecondary: '#333333', // matches landing secondary-color
    textMuted: '#696969', // matches landing text-color
    textDisabled: '#94A3B8',
    textAccent: '#000052', // matches primary color
    
    // Border System (matching landing page)
    border: 'rgba(16, 16, 16, 0.10)', // matches landing border-color
    borderLight: '#F0F2F4',
    borderHover: 'rgba(16, 16, 16, 0.15)',
    borderFocus: '#000052',
    
    // Data Visualization Palette
    chart: {
      primary: '#000052',
      secondary: '#00003D',
      tertiary: '#F59E0B',
      quaternary: '#EF4444',
      quinary: '#10B981',
      neutral: '#6B7280',
    },
    
    // Landing page style gradients
    gradient: {
      primary: 'linear-gradient(135deg, #000052 0%, #000047 100%)',
      secondary: 'linear-gradient(45deg, #F8FAFB 0%, #F0F2F4 100%)',
      hero: 'linear-gradient(135deg, #000052 0%, #00003D 100%)',
    },
  },
  
  typography: {
    fontFamily: {
      primary: '"Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', // matches landing body font
      mono: '"JetBrains Mono", "Fira Code", Consolas, Monaco, monospace',
      display: '"Plus Jakarta Sans", "Manrope", sans-serif', // matches landing heading font
    },
    fontSize: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem', // 36px
      '5xl': '3rem',    // 48px
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,     // Headings
      normal: 1.5,     // Body text
      relaxed: 1.625,  // Large blocks
    },
    letterSpacing: {
      tight: '-0.025em',
      normal: '0',
      wide: '0.025em',
    },
  },
  
  spacing: {
    0: '0',
    1: '0.25rem',
    2: '0.5rem',
    3: '0.75rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    8: '2rem',
    10: '2.5rem',
    12: '3rem',
    16: '4rem',
    20: '5rem',
    24: '6rem',
  },
  
  borderRadius: {
    none: '0',
    sm: '0.25rem',   // 4px
    base: '0.375rem', // 6px
    md: '0.5rem',     // 8px
    lg: '0.75rem',    // 12px
    xl: '1rem',       // 16px
    full: '9999px',
  },
  
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px rgba(0, 0, 0, 0.05), 0 10px 15px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1), 0 20px 25px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1), 0 25px 50px rgba(0, 0, 0, 0.25)',
    
    // Focus and interaction shadows
    focus: '0 0 0 3px rgba(14, 165, 233, 0.1)',
    focusRing: '0 0 0 2px #0EA5E9, 0 0 0 4px rgba(14, 165, 233, 0.1)',
    
    // Colored shadows for actions
    primary: '0 4px 6px rgba(14, 165, 233, 0.25)',
    success: '0 4px 6px rgba(34, 197, 94, 0.25)',
    warning: '0 4px 6px rgba(234, 179, 8, 0.25)',
    error: '0 4px 6px rgba(239, 68, 68, 0.25)',
  },
  
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
  
  transitions: {
    fast: '0.15s ease-in-out',
    base: '0.2s ease-in-out',
    slow: '0.3s ease-in-out',
  },

  // Layout constants
  layout: {
    sidebarWidth: '280px',
    sidebarCollapsed: '80px',
    containerMaxWidth: '1600px',
    contentPadding: '1.5rem',
    sectionGap: '3rem',
  },

  // Z-index scale
  zIndex: {
    hide: -1,
    auto: 'auto',
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
  },
};